import React, { useCallback, useEffect, useMemo } from 'react'
import { useKeyPress } from 'hooks/useKeyPress'
import flatten from 'lodash/fp/flatten'
import NumpadInputBiometricButton from 'redesign/components/atoms/Buttons/numpad-buttons/numpad-input-biometric-button'
import NumpadInputButton from 'redesign/components/atoms/Buttons/numpad-buttons/numpad-input-button'
import { ChevronRight } from 'redesign/components/atoms/Icon'
import { ONE, ZERO } from 'shared/constants'
import { BACKSPACE_KEY, ENTER_KEY, LAST_ARRAY_ELEMENT_INDEX, NUMBERS, PLUS_KEY, ZERO_KEY } from './constants'
import type { NumpadProps } from './types'

export const useNumpad = ({
  allowEnter,
  numpadType,
  numpadValue,
  usedWithPhoneNumberValidation,
  onConfirm,
  onNumpadValueChange,
  outputLength,
  setNumpadValue,
}: NumpadProps) => {
  const validKeyboardKeys = useMemo(() => {
    const keyList =
      numpadType === 'phone' ? [...NUMBERS, ZERO_KEY, PLUS_KEY, BACKSPACE_KEY] : [...NUMBERS, ZERO_KEY, BACKSPACE_KEY]

    return allowEnter ? [...keyList, ENTER_KEY] : keyList
  }, [allowEnter, numpadType])
  const ignoredKeys = usedWithPhoneNumberValidation ? [BACKSPACE_KEY] : []

  const handleInputClick = useCallback(
    (input: string) => {
      if (input === BACKSPACE_KEY) {
        return setNumpadValue(numpadValue.slice(ZERO, numpadValue.length - ONE))
      }
      if (allowEnter && input === ENTER_KEY && onConfirm) {
        return onConfirm()
      }
      if (outputLength) {
        return numpadValue.length < outputLength && setNumpadValue(numpadValue.concat(input))
      }

      return setNumpadValue(numpadValue.concat(input))
    },
    [allowEnter, numpadValue, onConfirm, outputLength, setNumpadValue],
  )

  const renderBottomLeftButton = useCallback(() => {
    if (numpadType === 'pin' && onConfirm) {
      return (
        <NumpadInputButton
          ariaLabel="Numpad confirm"
          testId="numpad-confirm"
          buttonType="submit"
          icon={<ChevronRight />}
          onClick={onConfirm}
        />
      )
    }

    if (numpadType === 'phone') {
      return <NumpadInputButton input={PLUS_KEY} onClick={() => handleInputClick(PLUS_KEY)} />
    }

    if (numpadType === 'pinLogin') {
      return <NumpadInputBiometricButton setNumpadValue={setNumpadValue} />
    }

    return <NumpadInputButton testId="empty-input-button" onClick={() => {}} />
  }, [handleInputClick, numpadType, onConfirm, setNumpadValue])

  const handleDeleteClick = useCallback(() => {
    numpadValue.length > ZERO && setNumpadValue(numpadValue.slice(ZERO, LAST_ARRAY_ELEMENT_INDEX))
  }, [numpadValue, setNumpadValue])

  useKeyPress(flatten(validKeyboardKeys), ignoredKeys, handleInputClick)

  useEffect(() => {
    !usedWithPhoneNumberValidation && setNumpadValue('')
  }, [outputLength, setNumpadValue, usedWithPhoneNumberValidation])

  useEffect(() => {
    if (onNumpadValueChange) {
      onNumpadValueChange(numpadValue)
    }
  }, [numpadValue, onNumpadValueChange])

  return {
    handleInputClick,
    handleDeleteClick,
    renderBottomLeftButton,
  }
}
